import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { stageRig } from '../lighting/stage-light-rig.js';
import { StormLayer } from '../particle-layers/storm-layer.js';

// Shared-rig contract (see the framing addendum): this visualizer's
// declared relationship to the one shared StageLightRig instance, and the
// beam intensity it asks for. Most restrictive wins across owners.
const RIG = 'required';
const RIG_OWNER = 'particlestorm';
const RIG_INTENSITY_SCALE = 1;
const RIG_ZONES = 'default';
// Same lit-floor treatment as neon-pulse.js/kinetic-rig.js: relaxes the rig's
// spot falloff (decay 2.4 -> 1.1) so beams actually reach the ground and draw
// pools on it instead of arriving divided by ~170.
const RIG_FLOOR_LIGHT_BOOST = 3;
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js). Disabled: this
// is only an isolated preview of the 'storm' layer already in the
// particles family, not a distinct visual - kept in the registry (not
// deleted) so the entry stays visible in ?candidates=1 rather than
// disappearing silently.
export const PROFILE = {
  id: 'particlestorm',
  family: 'particles',
  energyLevel: 0.35,
  bpmMin: 60,
  bpmMax: 118,
  movement: 0.40,
  danceability: 0.70,
  tonality: 0.50,
  enabled: false
};

// Standalone wrapper around StormLayer (see particle-layers/storm-
// layer.js for the actual particle logic) with its own camera/rig/floor,
// kept in the visualizer switcher for isolated tuning - NeonPulse now
// uses the same layer class as one of its rotating particle layers under
// a persistent shared rig, rather than duplicating this logic.
export class ParticleStormVisualizer extends Visualizer {
  init(scene, camera) {
    this._scene = scene;
    camera.position.set(0, 2.4, 7.5);
    camera.lookAt(0, 1, 0);

    this._rig = stageRig.acquire(RIG_OWNER, {
      scene: scene,
      intensityScale: RIG_INTENSITY_SCALE,
      zones: RIG_ZONES,
      floorLightBoost: RIG_FLOOR_LIGHT_BOOST
    });

    const floorGeometry = new THREE.PlaneGeometry(60, 60);
    const floorMaterial = new THREE.MeshStandardMaterial({ color: 0x16161c, roughness: 0.78, metalness: 0.0 });
    this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    scene.add(this._floor);

    this._layer = new StormLayer();
    this._layer.init(scene, camera);
  }

  update(deltaTime, profile) {
    stageRig.update(deltaTime, profile, RIG_OWNER);
    this._layer.update(deltaTime, profile, this._rig);
  }

  getLightRig() {
    return this._rig;
  }

  dispose() {
    stageRig.release(RIG_OWNER);
    this._rig = null;
    this._scene.remove(this._floor);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._layer.dispose(this._scene);
  }
}
